import { BaasHttp } from '@/http/axios';

/**
 * Busca a conta BAAS (endpoint usado pelo app React Native e feat/banking-main)
 * Compatível com ambas as branches
 * @param {Object} params - Parâmetros opcionais
 * @returns {Promise<Object>} Dados da conta bancária com proposal_status
 */
export const getAccount = async (params = {}) => {
  const response = await BaasHttp.get('/baas/get-account/', { params });
  return response.data;
};

/**
 * Busca a lista de contas BAAS vinculadas ao usuário
 * @param {Object} params - Parâmetros opcionais
 * @returns {Promise<Object>} Lista de contas bancárias
 */
export const getAccountList = async (params = {}) => {
  const response = await BaasHttp.get('/baas/get-account-list/', { params });
  return response.data;
};

/**
 * Alias para compatibilidade com feat/banking-main
 * @see getAccount
 */
export const getBankingAccount = getAccount;

/**
 * Busca o saldo da conta BAAS
 * @param {Object} params - Parâmetros opcionais
 * @returns {Promise<Object>} Dados do saldo
 */
export const getBalance = async (params = {}) => {
  const response = await BaasHttp.get('/baas/get-balance/', { params });
  return response.data;
};

export const createOperationPassword = async ({
  password,
  passwordConfirmation,
}) => {
  const response = await BaasHttp.post('/baas/create-operation-password/', {
    operationPassword: password,
    operationPasswordConfirmation: passwordConfirmation,
  });
  return response.data;
};

export const createAccountPin = async ({
  pin,
  password,
  accountId,
}) => {
  const resolvedPin = pin ?? password;
  const hasAccountId = accountId !== null && accountId !== undefined && accountId !== '' && accountId !== 0;
  const response = await BaasHttp.post(
    '/baas/create-account-pin/',
    { pin: resolvedPin },
    hasAccountId ? { params: { account_id: accountId } } : undefined
  );
  return response.data;
};

/**
 * Cria conta bancária para Pessoa Física
 * @param {Object} payload - Dados da conta PF
 * @returns {Promise<Object>} Resposta da criação
 */
export const createAccountPF = async (payload) => {
  const response = await BaasHttp.post('/baas/create-account-pf/', payload);
  return response.data;
};

/**
 * Cria conta bancária para Pessoa Jurídica (fluxo legado)
 * @param {Object} payload - Dados completos da conta PJ
 * @returns {Promise<Object>} Resposta da criação
 */
export const createAccountPJ = async (payload) => {
  const response = await BaasHttp.post('/baas/create-account/', payload);
  return response.data;
};



/**
 * Consulta preview da pré-proposta PJ a partir do CNPJ
 * Usado para preencher dados da empresa e sócios no fluxo KYC
 * @param {string} documentNumber - CNPJ sem máscara
 * @returns {Promise<Object>} Dados da pré-proposta enriquecida
 */
export const getPreproposalPreview = async (documentNumber) => {
  const response = await BaasHttp.get('/baas/preproposal-preview/', {
    params: { documentNumber },
  });
  return response.data;
};

/**
 * Persiste a revisão dos dados da empresa no backend
 * Atualiza informações como nome fantasia, tipo de empresa e endereço
 * @param {Object} payload - Dados revisados da empresa
 * @param {string} payload.documentNumber - CNPJ sem máscara
 * @param {string} payload.tradingName - Nome fantasia
 * @param {string} payload.companyType - Tipo de empresa
 * @param {string} payload.declaredCompanyRevenue - Faixa de faturamento
 * @param {Object} payload.businessAddress - Endereço da empresa
 * @returns {Promise<Object>} Preview atualizado
 */
export const updatePreproposalCompanyData = async (payload) => {
  const response = await BaasHttp.put('/baas/preproposal-company-data/', payload);
  return response.data;
};

/**
 * Cria conta PJ a partir de uma pré-proposta existente
 * Este fluxo NÃO envia payload completo, apenas o CNPJ
 * @param {Object} payload
 * @param {string} payload.documentNumber - CNPJ sem máscara
 * @returns {Promise<Object>} Status inicial da proposta
 */
export const createAccountPJFromPreproposal = async ({ documentNumber }) => {
  const response = await BaasHttp.post(
    '/baas/create-account-from-preproposal/',
    { documentNumber }
  );
  return response.data;
};

/**
 * Verifica CNPJ e cria/enriquece pré-proposta via Serpro
 * @param {string} documentNumber
 * @returns {Promise<Object>}
 */
export const verifyPJ = async (documentNumber) => {
  const response = await BaasHttp.post('/baas/verify-pj/', {
    documentNumber,
  });
  return response.data;
};

export const getPixCashoutStatus = async ({ id }) => {
  const response = await BaasHttp.get(`/baas/get-pix-cashout/${id}/`);
  return response.data;
};

export const getPixKeys = async () => {
  const response = await BaasHttp.get('/baas/get-pix-key/');
  return response.data;
};

export const getPixPortabilityStatus = async ({ accountId } = {}) => {
  const response = await BaasHttp.get('/baas/get-status-portability/', {
    params: accountId ? { account_id: accountId } : undefined,
  });
  return response.data;
};

export const getPixPortabilityDetails = async ({ id, accountId }) => {
  const response = await BaasHttp.get(`/baas/get-pix-portability/${id}/`, {
    params: accountId ? { account_id: accountId } : undefined,
  });
  return response.data;
};

export const requestPixKeyPortability = async ({
  key,
  keyType,
  accountId,
  claimType = 'PORTABILITY',
}) => {
  const normalizedKeyType = keyType === 'CNPJ' ? 'CPF' : keyType;
  const normalizedKey =
    keyType === 'CPF' || keyType === 'CNPJ' ? key.replace(/\D/g, '') : key.trim();
  const response = await BaasHttp.post(
    '/baas/pix-request-portability/',
    {
      key: normalizedKey,
      keyType: normalizedKeyType,
      claimType,
    },
    {
      params: accountId ? { account_id: accountId } : undefined,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return response.data;
};

export const confirmPixKeyPortability = async ({
  id,
  reason = 'USER_REQUESTED',
  accountId,
}) => {
  const response = await BaasHttp.post(
    '/baas/pix-confirm-portability/',
    { id, reason },
    {
      params: accountId ? { account_id: accountId } : undefined,
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return response.data;
};

export const deletePixKey = async ({ key, account }) => {
  if (!account) {
    throw new Error('A conta vinculada à chave Pix é obrigatória.');
  }

  const response = await BaasHttp.delete(`/baas/delete-pix-key/${encodeURIComponent(key)}/`, {
    data: { account },
  });
  return response.data;
};

export const verifyPixKeyEmailOtp = async ({ key, otpCode }) => {
  const response = await BaasHttp.post('/baas/verify-pix-key-email-otp/', {
    key,
    otpCode,
  });
  return response.data;
};

export const verifyPixKeyPhoneOtp = async ({ key, otpCode }) => {
  const response = await BaasHttp.post('/baas/verify-pix-key-phone-otp/', {
    key,
    otpCode,
  });
  return response.data;
};

export const getPixRecentContacts = async () => {
  const response = await BaasHttp.get('/baas/pix-recent-contacts/');
  return response.data;
};

export const getPixFavoriteContacts = async () => {
  const response = await BaasHttp.get('/baas/pix-favorite-contacts/');
  return response.data;
};

export const createPixFavoriteContact = async ({ pixContactId }) => {
  const response = await BaasHttp.post('/baas/pix-favorite-contacts/', {
    pixContactId,
  });
  return response.data;
};

export const deletePixFavoriteContact = async ({ pixContactId }) => {
  const response = await BaasHttp.delete(`/baas/pix-favorite-contacts/${pixContactId}/`);
  return response.data;
};

export const requestIncreasePixLimits = async ({
  limitType,
  limit_type,
  requestedLimit,
  requested_limit,
}) => {
  const response = await BaasHttp.post('/baas/request-increase-pix-limits/', {
    limit_type: limit_type || limitType,
    requested_limit: requested_limit || requestedLimit,
  });
  return response.data;
};
